import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, AlertTriangle, Settings, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useBusiness } from '../contexts/BusinessContext';

const AdminSubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    subscription, 
    isActive, 
    isLoading, 
    error, 
    plans,
    activateSubscription,
    refreshSubscription
  } = useSubscription();
  
  const { selectedBusiness } = useBusiness();
  const [activatingPlan, setActivatingPlan] = useState<string | null>(null);
  const [fromSignup, setFromSignup] = useState(false);
  const [signupData, setSignupData] = useState<any>(null);

  // Handle signup flow data
  useEffect(() => {
    const { fromSignup: isFromSignup, userId, email, message } = location.state || {};
    if (isFromSignup) {
      setFromSignup(true);
      setSignupData({ userId, email, message });
      console.log('📋 User coming from signup flow:', { userId, email });
    }
  }, [location.state]);

  const handleActivatePlan = async (planId: string) => {
    setActivatingPlan(planId);
    try {
      const success = await activateSubscription(planId);
      if (success) {
        const selectedPlan = plans.find(plan => plan.id === planId);
        console.log(`🎉 Successfully activated ${selectedPlan?.name} subscription!`);
        
        if (fromSignup && signupData) {
          // User is coming from signup, redirect to setup flow
          console.log('🔄 Redirecting to setup flow after signup payment');
          navigate('/setup', { 
            state: { 
              userId: signupData.userId,
              email: signupData.email,
              message: 'Payment successful! Let\'s complete your setup.' 
            }
          });
        } else {
          // Regular subscription activation, redirect to main app
          window.location.href = '/';
        }
      }
    } finally {
      setActivatingPlan(null);
    }
  };

  const getStatusIcon = () => {
    if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-gray-400" />;
    if (isActive) return <CheckCircle className="h-5 w-5 text-green-500" />;
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = () => {
    if (isLoading) return 'Loading...';
    if (isActive) return 'Active';
    return 'Inactive';
  };

  const getStatusColor = () => {
    if (isLoading) return 'text-gray-500';
    if (isActive) return 'text-green-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-4xl w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Settings className="h-8 w-8 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {fromSignup ? 'Complete Your Setup' : 'Subscription Management'}
          </h1>
          <p className="text-gray-600">
            {fromSignup 
              ? 'Please activate your subscription to continue with the setup process.'
              : 'Your business subscription is currently inactive. Please select a plan to restore access.'
            }
          </p>
          {fromSignup && signupData?.message && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
              <p className="text-green-800 text-sm">{signupData.message}</p>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-3" />
              <span className="text-red-700 font-medium">Error: {error}</span>
            </div>
          </div>
        )}

        {/* Current Status */}
        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CreditCard className="h-6 w-6 text-gray-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Current Status</h3>
                <p className="text-sm text-gray-500">Business subscription status</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon()}
              <span className={`font-semibold ${getStatusColor()}`}>
                {getStatusText()}
              </span>
            </div>
          </div>

          {subscription && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500">Status</p>
                <p className="text-lg font-semibold text-gray-900 capitalize">
                  {subscription.subscription_status}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500">Last Checked</p>
                <p className="text-lg font-semibold text-gray-900">
                  {subscription.subscription_last_checked 
                    ? new Date(subscription.subscription_last_checked).toLocaleDateString()
                    : 'Never'
                  }
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Available Plans */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Select a Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div key={plan.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">${plan.price}</div>
                    <div className="text-sm text-gray-500">per {plan.interval}</div>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.slice(0, 4).map((feature, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                  {plan.features.length > 4 && (
                    <li className="text-sm text-gray-500">
                      +{plan.features.length - 4} more features
                    </li>
                  )}
                </ul>

                <button
                  onClick={() => handleActivatePlan(plan.id)}
                  disabled={isLoading || activatingPlan === plan.id}
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-all duration-200 active:scale-95 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {activatingPlan === plan.id ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Activating...
                    </>
                  ) : (
                    'Activate Plan'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Important Information</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Activating a plan will immediately restore access for all team members</li>
            <li>• All team members inherit the business subscription status</li>
            <li>• Changes take effect immediately</li>
            <li>• You can manage your subscription from the Settings page once activated</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptionManagement; 